// Epoch 19: Lead Generator Enhancements - Mobile Filter Overlay Component
// File: src/components/filters/MobileFilterOverlay.tsx
// Purpose: Full-screen mobile filter interface with native mobile UX patterns

import React, { useState } from 'react';
import { MobileFilterOverlayProps } from '../../types/filters';
import FilterCategory from './FilterCategory';
import TierSelectionCategory from './TierSelectionCategory';

export default function MobileFilterOverlay({
  isOpen,
  onClose,
  filters,
  metadata,
  onToggleOption,
  onClearFilter,
  onClearAll
}: Omit<MobileFilterOverlayProps, 'onFilterChange'>) {
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const filterCategories = [
    { key: 'countries' as const, label: 'Country', searchable: true, defaultExpanded: false },
    { key: 'cities' as const, label: 'City', searchable: true, defaultExpanded: false },
    { key: 'jobLevels' as const, label: 'Job Level', searchable: false, defaultExpanded: false },
    { key: 'jobFunctions' as const, label: 'Job Function', searchable: false, defaultExpanded: false },
    { key: 'industries' as const, label: 'Industry', searchable: true, defaultExpanded: false }
  ];

  const getTotalFilterCount = () => {
    return filters.countries.length + 
           filters.cities.length + 
           filters.jobLevels.length + 
           filters.jobFunctions.length + 
           filters.industries.length + 
           (5 - filters.tiers.length); // Count deselected tiers
  };

  const handleApplyFilters = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Overlay Panel */}
      <div className="absolute inset-0 flex">
        <div className="w-full bg-white flex flex-col animate-in slide-in-from-bottom duration-300">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
            <div className="flex items-center space-x-3">
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
              {getTotalFilterCount() > 0 && (
                <span className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full font-medium">
                  {getTotalFilterCount()} active
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close filters"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex space-x-3">
              <button
                onClick={onClearAll}
                className="flex-1 py-2 px-3 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors font-medium"
              >
                Clear All
              </button>
              <button
                onClick={handleApplyFilters}
                className="flex-1 py-2 px-3 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium"
              >
                Apply Filters
              </button>
            </div>
          </div>

          {/* Filter Content - Scrollable */}
          <div className="flex-1 overflow-y-auto pb-safe">
            <div className="p-4 space-y-6">
              {filterCategories.map((category) => (
                <FilterCategory
                  key={category.key}
                  category={category}
                  selectedValues={filters[category.key] as string[]}
                  options={metadata?.[category.key] || []}
                  onToggleOption={(value) => onToggleOption(category.key, value)}
                  onClearCategory={() => onClearFilter(category.key)}
                  searchTerm={searchTerms[category.key] || ''}
                  onSearchChange={(term) => setSearchTerms(prev => ({ ...prev, [category.key]: term }))}
                />
              ))}
              
              {/* Tier Selection */}
              <TierSelectionCategory
                selectedTiers={filters.tiers}
                onToggleTier={(tier) => onToggleOption('tiers', tier)}
                onClearTiers={() => onClearFilter('tiers')}
              />
              
              {/* Extra padding for safe area */}
              <div className="h-4"></div>
            </div>
          </div>

          {/* Footer - Sticky */}
          <div className="p-4 border-t border-gray-200 bg-white safe-area-bottom">
            <button
              onClick={handleApplyFilters}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors text-center"
            >
              Apply Filters & Preview Leads
            </button>
            
            {/* Applied filters summary */}
            {getTotalFilterCount() > 0 && (
              <div className="text-center text-sm text-gray-600 mt-2">
                {getTotalFilterCount()} {getTotalFilterCount() === 1 ? 'filter' : 'filters'} will be applied
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}